import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router"
import { useAuth } from "@/composables/auth/useAuth"

const routes: RouteRecordRaw[] = [
  {
    path: "",
    component: () => import("@/views/HomeView.vue"),
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: { name: "Home" } },
      {
        path: "board",
        name: "Home",
        component: () => import("@/views/pages/books/BoardView.vue"),
      },
      {
        path: "books",
        name: "Books",
        component: () => import("@/views/pages/books/BooksView.vue"),
      },
      {
        path: "/book/:book_id",
        name: "Book",
        component: () => import("@/views/pages/books/BookView.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "search",
        name: "Search Libary",
        component: () => import("@/views/pages/books/SearchView.vue"),
      },
      {
        path: "bookclub",
        name: "Book Club",
        component: () => import("@/views/pages/bookclub/BookClubView.vue"),
      },
      {
        path: "profile",
        name: "Profile",
        component: () => import("@/views/pages/profile/ProfileView.vue"),
      },
      {
        path: "profile/:username",
        name: "UserProfile",
        component: () => import("@/views/pages/profile/UserProfileView.vue"),
      },
      {
        path: "profile/edit",
        name: "ProfileEdit",
        component: () => import("@/views/pages/profile/ProfileEditView.vue"),
      },
      {
        path: "settings",
        name: "Settings",
        component: () => import("@/views/pages/settings/SettingsView.vue"),
      },
      {
        path: "settings/account",
        name: "Account",
        component: () => import("@/views/pages/settings/AccountView.vue"),
      },
      {
        path: "settings/security",
        name: "Security",
        component: () => import("@/views/pages/settings/SecurityView.vue"),
      },
      {
        path: "settings/subscription",
        name: "Subscription",
        component: () => import("@/views/pages/settings/SubscriptionView.vue"),
      },
      {
        path: "settings/appearance",
        name: "Appearance",
        component: () => import("@/views/pages/settings/AppearanceView.vue"),
      },
      {
        path: "settings/app-info",
        name: "AppInfo",
        component: () => import("@/views/pages/settings/AppInfoView.vue"),
      },
      {
        path: "settings/activity-privacy",
        name: "PrivacySettings",
        component: () => import("@/views/pages/settings/ActivityPrivacyView.vue"),
      },
      {
        path: "settings/social-privacy",
        name: "SocialPrivacy",
        component: () => import("@/views/pages/settings/SocialPrivacyView.vue"),
      },
      {
        path: "update-password",
        name: "UpdatePassword",
        component: () => import("@/views/pages/auth/UpdatePasswordView.vue"),
      },
      {
        path: "confirm-email-change",
        name: "ConfirmEmailChange",
        component: () => import("@/views/pages/auth/ConfirmEmailChangeView.vue"),
      },
      {
        path: "privacy",
        name: "PrivacyPolicy",
        component: () => import("@/views/pages/legal/PrivacyView.vue"),
      },
      {
        path: "lists",
        name: "Lists",
        component: () => import("@/views/pages/lists/ListsView.vue"),
      },
      {
        path: "/list/:list_id",
        name: "List",
        component: () => import("@/views/pages/lists/ListView.vue"),
        // No requiresAuth - public lists should be viewable by anyone
        // ListView handles permission checks internally
      },
      {
        path: "lookup",
        name: "Lookup",
        component: () => import("@/views/pages/books/LookupView.vue"),
      },
      {
        path: "activity",
        name: "Activity",
        component: () => import("@/views/pages/activity/ActivityFeedView.vue"),
      },
    ],
  },
  {
    path: "/login",
    component: () => import("@/views/AuthView.vue"),
    children: [
      {
        path: "",
        name: "Auth",
        component: () => import("@/views/login/LoginView.vue"),
      },
    ],
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () => import("@/views/login/ResetPasswordView.vue"),
    meta: { requiresAuth: false },
  },
  { path: "/:pathMatch(.*)*", redirect: "/" },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

/**
 * SECURITY: Validate redirect URL to prevent open redirect attacks
 * Only allow redirects to internal paths (same origin)
 */
function isValidRedirect(url: string): boolean {
  try {
    // If it's a relative path (starts with /), it's safe
    if (url.startsWith("/")) {
      // Check it doesn't try to escape to external domain via //
      if (url.startsWith("//")) {
        return false
      }
      return true
    }

    // If it's a full URL, validate it's same origin
    const parsed = new URL(url, window.location.origin)
    return parsed.origin === window.location.origin
  } catch {
    // Invalid URL format
    return false
  }
}

router.beforeEach(async (to) => {
  const { user, ensureReady } = useAuth()
  await ensureReady()
  if (to.meta.requiresAuth && !user.value) {
    // SECURITY: Validate redirect parameter to prevent open redirect
    const redirectUrl = to.fullPath
    const safeRedirect = isValidRedirect(redirectUrl) ? redirectUrl : "/books"
    return { name: "Auth", query: { redirect: safeRedirect } }
  }
  if (to.name === "Auth" && user.value) {
    return { name: "Home" }
  }
})

export default router
